import { isPlainRecord } from "@/utils/safeRecordSanitization";
import { summarizeBindingContextForAi } from "./BindingContextSummary";
import type {
  AiBindingContextComponentInput,
  AiBindingContextSummary,
} from "./BindingContextSummary";
import type { Component } from "@/types";

const FORBIDDEN_AI_PROPERTY_KEY_NAMES = [
  "__proto__",
  "constructor",
  "prototype",
  "data",
  "sampleData",
  "dataSource",
  "rawData",
  "rawDataset",
  "dataset",
  "datasets",
  "rows",
  "rawRows",
  "records",
  "resultRows",
  "runtimeRows",
  "safeRuntimeRows",
  "aiSafeRuntimeRows",
  "aiContextSafeRuntimeRows",
  "rawSource",
  "rawSourceBlob",
  "sourceBlob",
  "sourceBlobs",
  "blob",
  "blobs",
];
const FORBIDDEN_AI_PROPERTY_KEYS = new Set(
  FORBIDDEN_AI_PROPERTY_KEY_NAMES.map((key) => key.toLowerCase()),
);
const SENSITIVE_AI_PROPERTY_KEY_PATTERN =
  /token|password|secret|authorization|header|credential/i;

export interface SafeAiComponentContextInput {
  id: string;
  type: string;
  name?: string;
  properties: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}

export interface SafeAiComponentContextSummary {
  properties: Record<string, unknown>;
  bindingContext?: AiBindingContextSummary;
}

export function sanitizeComponentPropertiesForAi(
  properties: Record<string, unknown>,
): Record<string, unknown> {
  return sanitizeAiPropertyRecord(properties, new WeakMap());
}

export function buildSafeAiComponentContext(
  component: SafeAiComponentContextInput,
  relatedComponents: SafeAiComponentContextInput[] = [],
): SafeAiComponentContextSummary {
  const properties = sanitizeComponentPropertiesForAi(component.properties);
  const bindingContext = summarizeSafeBindingContext(component, relatedComponents);

  return bindingContext === undefined
    ? { properties }
    : { properties, bindingContext };
}

export function buildSafeAiComponentContextFromComponent(
  component: Component,
  relatedComponents: Component[] = [],
): SafeAiComponentContextSummary {
  return buildSafeAiComponentContext(
    {
      id: component.id,
      type: component.type,
      name: component.name,
      properties: component.properties,
      metadata: component.metadata,
    },
    relatedComponents.map((relatedComponent) => ({
      id: relatedComponent.id,
      type: relatedComponent.type,
      name: relatedComponent.name,
      properties: relatedComponent.properties,
      metadata: relatedComponent.metadata,
    })),
  );
}

function summarizeSafeBindingContext(
  component: SafeAiComponentContextInput,
  relatedComponents: SafeAiComponentContextInput[],
): AiBindingContextSummary | undefined {
  if (component.properties.dataBinding === undefined) {
    return undefined;
  }

  const bindingInput: AiBindingContextComponentInput = {
    id: component.id,
    type: component.type,
    name: component.name,
    properties: component.properties,
    metadata: component.metadata,
  };

  return summarizeBindingContextForAi(bindingInput, {
    relatedComponents: relatedComponents.map((relatedComponent) => ({
      id: relatedComponent.id,
      type: relatedComponent.type,
      name: relatedComponent.name,
      properties: relatedComponent.properties,
      metadata: relatedComponent.metadata,
    })),
  });
}

function sanitizeAiPropertyValue(
  value: unknown,
  seen: WeakMap<object, unknown>,
): unknown {
  if (Array.isArray(value)) {
    const existing = seen.get(value);
    if (existing !== undefined) {
      return existing;
    }

    const sanitizedItems: unknown[] = [];
    seen.set(value, sanitizedItems);
    for (const item of value) {
      sanitizedItems.push(sanitizeAiPropertyValue(item, seen));
    }
    return sanitizedItems;
  }

  if (!isPlainRecord(value)) {
    return value;
  }

  return sanitizeAiPropertyRecord(value, seen);
}

function sanitizeAiPropertyRecord(
  value: Record<string, unknown>,
  seen: WeakMap<object, unknown>,
): Record<string, unknown> {
  const existing = seen.get(value);
  if (existing !== undefined && isPlainRecord(existing)) {
    return existing;
  }

  const sanitized: Record<string, unknown> = {};
  seen.set(value, sanitized);

  for (const [key, nestedValue] of Object.entries(value)) {
    if (!isSafeAiPropertyKey(key)) {
      continue;
    }

    sanitized[key] = sanitizeAiPropertyValue(nestedValue, seen);
  }

  return sanitized;
}

function isSafeAiPropertyKey(key: string): boolean {
  const normalizedKey = key.toLowerCase();
  if (FORBIDDEN_AI_PROPERTY_KEYS.has(normalizedKey)) {
    return false;
  }

  if (normalizedKey === "databinding") {
    return false;
  }

  return !SENSITIVE_AI_PROPERTY_KEY_PATTERN.test(key);
}
